import { useMemo } from "react";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { TYPOGRAPHY_KEYS, type TypographyStyles } from "./typographyTypes";

/**
 * Helper to extract string values from potentially nested block prop structures.
 * Block props can be primitives OR objects with a `value` property.
 */
const extractValue = (prop: unknown): string | undefined => {
  if (prop == null) return undefined;

  if (typeof prop === "object" && "value" in prop) {
    const { value } = prop as { value: unknown };
    // Only convert to string if value is a primitive (string or number)
    if (
      value != null &&
      (typeof value === "string" || typeof value === "number")
    ) {
      return String(value);
    }
    return undefined;
  }

  return typeof prop === "string" || typeof prop === "number"
    ? String(prop)
    : undefined;
};

export const useBlockStyleOverrides = (): TypographyStyles => {
  const selectedBlock = useBuilderStore(
    state => state.getSelectedBlock()?.block ?? null,
  );

  return useMemo(() => {
    const blockStyles = selectedBlock?.props?.styles?.value as
      | Record<string, unknown>
      | undefined;

    if (!blockStyles) {
      return {};
    }

    const overrides: TypographyStyles = {};

    for (const key of TYPOGRAPHY_KEYS) {
      const value = extractValue(blockStyles[key]);
      if (value) {
        overrides[key] = value;
      }
    }

    return overrides;
  }, [selectedBlock]);
};
